import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Pencil, Calendar, CheckCircle, XCircle } from "lucide-react";
import Loader from "components/Loaders/Loader";
import "./AddGoal.css";

const API_URL = process.env.REACT_APP_API_URL;

const AddGoal = () => {
    const navigate = useNavigate();
    const sessionUid = localStorage.getItem("session_uid") ?? "";
    const userUid = localStorage.getItem("user_uid") ?? "";
    
    const [newGoal, setNewGoal] = useState({
        goal_name: "",
        description: "",
        goal_metric_type: "steps",
        goal_metric_value: "",
        from_datetime: null as Date | null,
        to_datetime: null as Date | null,
        goal_category: "fitness",
        goal_type: "daily",
    });
    
    const [loading, setLoading] = useState(false);

    const formatDateToUTC = (date: Date | null): string => {
        if (!date) return "";
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
            .toISOString()
            .replace(/\.\d{3}Z$/, "Z"); // Remove milliseconds
    };
    
    const handleAddGoal = async () => {
        if (!newGoal.from_datetime || !newGoal.to_datetime) {
            alert("Please select valid start and end dates.");
            return;
        }

        const formattedGoal = {
            ...newGoal,
            from_datetime: formatDateToUTC(newGoal.from_datetime),
            to_datetime: formatDateToUTC(newGoal.to_datetime),
        };
    
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/api/create-goal`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-uid": userUid,
                }, credentials: "include",
                body: JSON.stringify(formattedGoal),
            });
    
            if (response.ok) {
                navigate("/goal-settings");
            } else {
                console.error("Failed to add goal");
            }
        } catch (error) {
            console.error("Error adding goal:", error);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="add-goal">
            {loading && <Loader />}
            <h2><Pencil size={24} /> Add a New Goal</h2>
            <div className="addGoal-form-group">
                <label>Goal Name</label>
                <input type="text" placeholder="Enter Goal Name" value={newGoal.goal_name} 
                    onChange={(e) => setNewGoal({ ...newGoal, goal_name: e.target.value })} disabled={loading} />
            </div>
            <div className="addGoal-form-group">
                <label>Description</label>
                <input type="text" placeholder="Enter Description" value={newGoal.description} 
                    onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })} disabled={loading} />
            </div>
            <div className="addGoal-form-group">
                <label>Metric Type</label>
                <select value={newGoal.goal_metric_type} 
                    onChange={(e) => setNewGoal({ ...newGoal, goal_metric_type: e.target.value })} disabled={loading}>
                    <option value="steps">Steps</option>
                    <option value="distance">Distance</option>
                    <option value="calories">Calories</option>
                </select>
            </div>
            <div className="addGoal-form-group">
                <label>Goal Value</label>
                <input type="number" placeholder="Enter Goal Value" value={newGoal.goal_metric_value} 
                    onChange={(e) => setNewGoal({ ...newGoal, goal_metric_value: e.target.value })} disabled={loading} />
            </div>
            <div className="addGoal-form-group">
                <label><Calendar size={16} /> From Date</label>
                <DatePicker selected={newGoal.from_datetime} 
                    onChange={(date) => setNewGoal({ ...newGoal, from_datetime: date })} 
                    showTimeSelect timeFormat="HH:mm" timeIntervals={15} 
                    dateFormat="yyyy-MM-dd HH:mm" className="date-picker" disabled={loading} />
            </div>
            <div className="addGoal-form-group">
                <label><Calendar size={16} /> To Date</label>
                <DatePicker selected={newGoal.to_datetime} 
                    onChange={(date) => setNewGoal({ ...newGoal, to_datetime: date })} 
                    showTimeSelect timeFormat="HH:mm" timeIntervals={15} 
                    dateFormat="yyyy-MM-dd HH:mm" className="date-picker" disabled={loading} />
            </div>
            {/* <div className="addGoal-form-group">
                <label>Goal Period</label>
                <select value={newGoal.goal_type} 
                    onChange={(e) => setNewGoal({ ...newGoal, goal_type: e.target.value })} disabled={loading}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                </select>
            </div> */}
            <div className="addGoal-button-group">
                <button onClick={handleAddGoal} className="addGoal-save-btn" disabled={loading}>
                    <CheckCircle size={18} /> {loading ? "Submitting..." : "Submit"}
                </button>
                <button onClick={() => navigate("/GoalSettings")} className="addGoal-cancel-btn" disabled={loading}>
                    <XCircle size={18} /> Cancel
                </button>
            </div>
        </div>
    );
};

export default AddGoal;
